<template>
  <div class="tc-shell" style="margin-top: 20px">

    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 20px; padding: 4px 12px;">
      <span style="font-size: 26px; font-weight: bold">Settings</span>
    </div>

    <div class="tc-layout">

      <!-- Collapseable left nav (horizon drives tabs from the sidebar via ?tab=) -->
      <div v-if="!horizonNavActive" class="tc-nav-wrapper" :class="{ 'tc-nav-collapsed': navCollapsed }">
        <div class="tc-nav-inner">
          <button
            v-for="tab in visibleTabs"
            :key="tab.id"
            class="tc-nav-item"
            :class="{ active: activeTab === tab.id }"
            @click="selectTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>
        <button
          class="tc-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <!-- Tab content -->
      <div class="tc-content">

        <!-- horizon-only landing: a card grid of the settings areas. no ref
             on purpose -- the shell's dirty/load_data plumbing skips it -->
        <div v-if="horizonNavActive" v-show="activeTab === 'tab_overview'" class="tc-overview">
          <p class="tc-ov-sub">Choose a settings area to manage.</p>
          <div class="tc-ov-grid">
            <button
              v-for="card in overviewCards"
              :key="card.id"
              class="tc-ov-card"
              @click="selectTab(card.id)"
            >
              <span class="tc-ov-icon"><i class="fa" :class="card.icon" aria-hidden="true"></i></span>
              <span class="tc-ov-body">
                <span class="tc-ov-title">{{ card.label }}</span>
                <span class="tc-ov-desc">{{ card.desc }}</span>
              </span>
            </button>
          </div>
        </div>

        <tenant-config-general
          v-show="activeTab === 'tab_general'"
          ref="tab_general"
          @showAlert="relayShowAlert"
        />

        <tenant-config-bookings-actuals
          v-if="showBookingsActuals"
          v-show="activeTab === 'tab_bookings_actuals'"
          ref="tab_bookings_actuals"
          @showAlert="relayShowAlert"
        />

        <tenant-config-cust-map-views
          v-show="activeTab === 'tab_cust_map_views'"
          ref="tab_cust_map_views"
          @showAlert="relayShowAlert"
        />

        <tenant-config-views-reports
          v-if="showViewsReports"
          v-show="activeTab === 'tab_views_reports'"
          ref="tab_views_reports"
          @showAlert="relayShowAlert"
        />

        <tenant-config-alerts
          v-if="showAlerts"
          v-show="activeTab === 'tab_alerts'"
          ref="tab_alerts"
          @showAlert="relayShowAlert"
        />

        <tenant-config-notifications
          v-if="showNotifications"
          v-show="activeTab === 'tab_notifications'"
          ref="tab_notifications"
          @showAlert="relayShowAlert"
        />

        <tenant-config-veh-status-mgmt
          v-if="showVSM"
          v-show="activeTab === 'tab_veh_status_mgmt'"
          ref="tab_veh_status_mgmt"
          @showAlert="relayShowAlert"
        />

        <tenant-config-bus-sos
          v-if="showBusSOS"
          v-show="activeTab === 'tab_bus_sos'"
          ref="tab_bus_sos"
          @showAlert="relayShowAlert"
        />

        <tenant-config-data-services
          v-if="showDataServices"
          v-show="activeTab === 'tab_data_services'"
          ref="tab_data_services"
          @showAlert="relayShowAlert"
        />

        <tenant-config-fleet-assistant
          v-if="showFleetAssistant"
          v-show="activeTab === 'tab_fleet_assistant'"
          ref="tab_fleet_assistant"
          @showAlert="relayShowAlert"
        />

        <tenant-config-history
          v-show="activeTab === 'tab_history'"
          ref="tab_history"
          @showAlert="relayShowAlert"
        />

        <user-list
          v-show="activeTab === 'tab_user_list'"
          ref="tab_user_list"
        />

      </div>
    </div>

    <b-modal
      id="tc-unsaved-modal"
      title="Unsaved Changes"
      ok-title="Leave without saving"
      cancel-title="Stay on this page"
      ok-variant="danger"
      @ok="confirmTabSwitch"
      @cancel="cancelTabSwitch"
      @hide="onUnsavedModalHide"
    >
      <p>You have unsaved changes on this page. If you continue, your changes will be lost.</p>
    </b-modal>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import horizonNavMixin from '@/nav/horizon_nav_mixin'
import TenantConfigGeneral from './tenant_config_general.vue'
import TenantConfigBookingsActuals from './tenant_config_bookings_actuals.vue'
import TenantConfigCustMapViews from './tenant_config_cust_map_views.vue'
import TenantConfigViewsReports from './tenant_config_views_reports.vue'
import TenantConfigAlerts from './tenant_config_alerts.vue'
import TenantConfigNotifications from './tenant_config_notifications.vue'
import TenantConfigVehStatusMgmt from './tenant_config_veh_status_mgmt.vue'
import TenantConfigBusSos from './tenant_config_bus_sos.vue'
import TenantConfigDataServices from './tenant_config_data_services.vue'
import TenantConfigFleetAssistant from './tenant_config_fleet_assistant.vue'
import TenantConfigHistory from './tenant_config_history.vue'
import UserList from './user_list.vue'

const TAB_LABELS = {
  tab_overview:         'Overview',
  tab_general:          'General',
  tab_bookings_actuals: 'Bookings & Actuals',
  tab_cust_map_views:   'Map Settings',
  tab_views_reports:    'Views & Reports',
  tab_alerts:           'Alerts',
  tab_notifications:    'Notifications',
  tab_veh_status_mgmt:  'Vehicle Status Mgr',
  tab_bus_sos:          'Bus SOS Settings',
  tab_data_services:    'Data Services',
  tab_fleet_assistant:  'Fleet Assistant',
  tab_history:          'History',
  tab_user_list:        'Manage Users',
}

// card copy for the horizon overview landing (no per-tab descriptions
// existed anywhere to reuse)
const TAB_OVERVIEW_META = {
  tab_general:          { icon: 'fa-sliders',              desc: 'Company profile, branding, and core account details.' },
  tab_bookings_actuals: { icon: 'fa-calendar-check-o',     desc: 'How bookings import and match against recorded actuals.' },
  tab_cust_map_views:   { icon: 'fa-map-o',                desc: 'Default map views, labels, and visibility options.' },
  tab_views_reports:    { icon: 'fa-bar-chart',            desc: 'Which report pages are available and how they behave.' },
  tab_alerts:           { icon: 'fa-bell-o',               desc: 'Alert rules and thresholds for fleet events.' },
  tab_notifications:    { icon: 'fa-envelope-o',           desc: 'Notification templates and recipient settings.' },
  tab_veh_status_mgmt:  { icon: 'fa-wrench',               desc: 'Status board, inspection, and maintenance options.' },
  tab_bus_sos:          { icon: 'fa-exclamation-triangle', desc: 'Emergency SOS recipients, range, and vehicle coverage.' },
  tab_data_services:    { icon: 'fa-exchange',             desc: 'Data sync services for drivers and vehicles.' },
  tab_fleet_assistant:  { icon: 'fa-magic',                desc: 'Fleet Assistant access and behavior for this account.' },
  tab_history:          { icon: 'fa-history',              desc: 'A log of settings changes on this account.' },
  tab_user_list:        { icon: 'fa-users',                desc: 'Add users, set roles, and manage access.' },
}

export default {
  name: 'tenant_config_shell',
  mixins: [horizonNavMixin],
  components: {
    TenantConfigGeneral,
    TenantConfigBookingsActuals,
    TenantConfigCustMapViews,
    TenantConfigViewsReports,
    TenantConfigAlerts,
    TenantConfigNotifications,
    TenantConfigVehStatusMgmt,
    TenantConfigBusSos,
    TenantConfigDataServices,
    TenantConfigFleetAssistant,
    TenantConfigHistory,
    UserList,
  },
  data() {
    return {
      activeTabIndex: 0,
      pendingTabIndex: null,
      pendingNavNext: null,
      modalOkClicked: false,
      navCollapsed: false,
      navUserToggled: false,
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.anyTabDirty) {
      this.pendingNavNext = next
      this.pendingTabIndex = null
      this.modalOkClicked = false
      this.$bvModal.show('tc-unsaved-modal')
    } else {
      next()
    }
  },
  mounted() {
    this._beforeUnloadHandler = (e) => {
      if (this.anyTabDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._beforeUnloadHandler)

    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()

    // classic restores the last tab; under horizon a bare /settings lands on
    // the overview (index 0) and the ?tab= query is the source of truth
    const saved = localStorage.getItem('tc_active_tab')
    if (saved && !this.horizonNavActive) {
      const idx = this.visibleTabRefKeys.indexOf(saved)
      if (idx !== -1) this.activeTabIndex = idx
    }
    // horizon sidebar deep-links tabs via ?tab= — the query beats the saved
    // tab, and the url is kept honest so the sidebar highlight always matches.
    // classic behavior is untouched (no query written unless horizon).
    const qTab = this.$route.query && this.$route.query.tab
    if (qTab) {
      const qIdx = this.visibleTabRefKeys.indexOf(qTab)
      if (qIdx !== -1) this.activeTabIndex = qIdx
    }
    if (this.horizonNavActive) this._syncTabQuery()
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler)
    window.removeEventListener('resize', this._onResize)
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    showBookingsActuals() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].actuals_enabled
    },
    showViewsReports() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].views_reports_enabled
    },
    showAlerts() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].alerts_enabled
    },
    showNotifications() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].alerts_enabled
    },
    showVSM() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].vehicle_status_management_enabled
    },
    showBusSOS() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].bus_sos_enabled
    },
    showDataServices() {
      return this.isEffectiveAdmin || this.user.features[this.user.tenant].data_services_enabled
    },
    showFleetAssistant() {
      return this.isEffectiveAdmin || (this.user.tenant === 'anderson-759g468f' && this.user.role === 'customer-admin')
    },
    visibleTabRefKeys() {
      const keys = []
      // the overview landing exists only under horizon; classic keeps its
      // own rail and lands on General exactly as before
      if (this.horizonNavActive) keys.push('tab_overview')
      keys.push('tab_general')
      if (this.showBookingsActuals) keys.push('tab_bookings_actuals')
      keys.push('tab_cust_map_views')
      if (this.showViewsReports) keys.push('tab_views_reports')
      if (this.showAlerts) keys.push('tab_alerts')
      if (this.showNotifications) keys.push('tab_notifications')
      if (this.showVSM) keys.push('tab_veh_status_mgmt')
      if (this.showBusSOS) keys.push('tab_bus_sos')
      if (this.showDataServices) keys.push('tab_data_services')
      if (this.showFleetAssistant) keys.push('tab_fleet_assistant')
      keys.push('tab_history')
      keys.push('tab_user_list')
      return keys
    },
    visibleTabs() {
      return this.visibleTabRefKeys.map(id => ({ id, label: TAB_LABELS[id] }))
    },
    overviewCards() {
      return this.visibleTabRefKeys
        .filter(id => id !== 'tab_overview')
        .map(id => ({
          id,
          label: TAB_LABELS[id],
          icon: (TAB_OVERVIEW_META[id] || {}).icon || 'fa-cog',
          desc: (TAB_OVERVIEW_META[id] || {}).desc || '',
        }))
    },
    activeTab() {
      return this.visibleTabRefKeys[this.activeTabIndex] || null
    },
    anyTabDirty() {
      return this.visibleTabRefKeys.some(key => {
        const ref = this.$refs[key]
        return ref && ref.isDirty
      })
    },
  },
  watch: {
    // horizon sidebar navigation lands here as a query change; route it
    // through selectTab so the unsaved-changes guard still runs
    '$route.query.tab'(val) {
      if (!val || val === this.activeTab) return
      if (this.visibleTabRefKeys.indexOf(val) === -1) return
      this.selectTab(val)
    },
    // forward-sync: a completed tab switch (direct or via the confirm modal)
    // updates the url so the sidebar highlight follows. horizon-only so
    // classic urls stay byte-identical.
    activeTab() {
      if (this.horizonNavActive) this._syncTabQuery()
    },
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
    relayShowAlert(msg, variant, autoclose) {
      this.$emit('showAlert', msg, variant, autoclose)
    },
    _syncTabQuery() {
      if (this.$route.name !== 'settings' || !this.activeTab) return
      if (this.$route.query.tab === this.activeTab) return
      this.$router.replace({ query: { ...this.$route.query, tab: this.activeTab } }).catch(() => {})
    },
    selectTab(id) {
      if (this._confirmingTabSwitch) return
      const newIndex = this.visibleTabRefKeys.indexOf(id)
      if (newIndex === this.activeTabIndex) return
      const prevRef = this.$refs[this.activeTab]
      if (prevRef && prevRef.isDirty) {
        this.pendingTabIndex = newIndex
        this.modalOkClicked = false
        this.$bvModal.show('tc-unsaved-modal')
      } else {
        this.activeTabIndex = newIndex
        // the overview is a horizon landing, not a real tab; keep classic's
        // saved-tab restore pointing at the last real tab
        if (id !== 'tab_overview') localStorage.setItem('tc_active_tab', id)
        this.$nextTick(() => {
          const ref = this.$refs[id]
          if (ref && ref.load_data) ref.load_data(true)
        })
      }
    },
    confirmTabSwitch() {
      this.modalOkClicked = true
      if (this.pendingNavNext) {
        const next = this.pendingNavNext
        this.pendingNavNext = null
        next()
      } else {
        const prevRef = this.$refs[this.activeTab]
        if (prevRef && prevRef.isDirty) {
          prevRef.load_data()
        }
        this._confirmingTabSwitch = true
        const destId = this.visibleTabRefKeys[this.pendingTabIndex]
        this.activeTabIndex = this.pendingTabIndex
        if (destId !== 'tab_overview') localStorage.setItem('tc_active_tab', destId)
        this.pendingTabIndex = null
        this.$nextTick(() => {
          this._confirmingTabSwitch = false
          const ref = this.$refs[destId]
          if (ref && ref.load_data) ref.load_data(true)
        })
      }
    },
    cancelTabSwitch() {
      if (this.pendingNavNext) {
        const next = this.pendingNavNext
        this.pendingNavNext = null
        next(false)
      }
      this.pendingTabIndex = null
      // the sidebar click already rewrote ?tab= before the guard fired;
      // put the url back on the tab the user chose to stay on
      if (this.horizonNavActive) this._syncTabQuery()
      const correctIndex = this.activeTabIndex
      setTimeout(() => {
        const focusedEl = document.activeElement
        if (focusedEl && this.$el.contains(focusedEl)) {
          focusedEl.blur()
        }
        const refKey = this.visibleTabRefKeys[correctIndex]
        const ref = refKey ? this.$refs[refKey] : null
        if (ref && ref.scrollToFirstDirtyField) {
          ref.scrollToFirstDirtyField()
        }
      }, 300)
    },
    onUnsavedModalHide() {
      if (!this.modalOkClicked) {
        this.pendingTabIndex = null
      }
      this.modalOkClicked = false
    },
  },
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.tc-layout {
  display: flex;
  align-items: flex-start;
}

/* ── Left nav wrapper (handles collapse transition) ─────────────────── */
.tc-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.tc-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.tc-nav-collapsed .tc-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

/* ── Nav items ──────────────────────────────────────────────────────── */
.tc-nav-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #333;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  outline: none;
}

.tc-nav-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 1px;
  background-color: #c8d8e8;
}

.tc-nav-item:last-child {
  border-bottom: none;
}

.tc-nav-item:hover {
  background-color: #d0dfee;
}

.tc-nav-item:hover::before {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  border: 1px solid #a0b8d0;
  pointer-events: none;
}

/* ── Horizon overview landing (card grid of settings areas) ─────────── */
.tc-overview {
  text-align: left;
  padding: 4px 2px;
}

.tc-ov-sub {
  color: #555;
  font-size: 14px;
  margin-bottom: 18px;
}

.tc-ov-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.tc-ov-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(70, 130, 180, 0.08);
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.tc-ov-card:hover {
  border-color: steelblue;
  box-shadow: 0 2px 8px rgba(70, 130, 180, 0.18);
}

.tc-ov-card:focus {
  outline: none;
}

.tc-ov-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(70, 130, 180, 0.35);
}

.tc-ov-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(70, 130, 180, 0.12);
  color: steelblue;
  font-size: 15px;
}

.tc-ov-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tc-ov-title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
}

.tc-ov-desc {
  font-size: 12.5px;
  color: #666;
  line-height: 1.4;
}

.tc-nav-item:focus {
  outline: none;
  box-shadow: none;
}

.tc-nav-item.active {
  background-color: var(--fi-rail-accent) !important;
  color: white !important;
  font-weight: bold;
  border-right: 3px solid #2e6da4;
}

.tc-nav-item.active::before {
  display: none;
}

/* ── Collapse toggle ────────────────────────────────────────────────── */
.tc-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.tc-nav-toggle:hover {
  background: #d0dfee;
  color: var(--fi-rail-accent);
}

.tc-nav-toggle:focus {
  outline: none;
}

/* ── Content area ───────────────────────────────────────────────────── */
.tc-content {
  flex: 1;
  min-width: 0;
  padding-left: 24px;
}
</style>
